import { useEffect } from "react";
import "../styles/TableSystem.css";

const EXCLUDED_TABLE_SELECTORS = [
  ".roadside-table",
  ".detail-table",
  ".posting-table",
  ".shift-table",
  "[data-no-enhance-table='true']",
].join(", ");

const ACTION_HEADERS = new Set(["action", "actions"]);

function getSortableTables() {
  return Array.from(document.querySelectorAll("table.table")).filter((table) => {
    if (EXCLUDED_TABLE_SELECTORS && table.matches(EXCLUDED_TABLE_SELECTORS)) {
      return false;
    }

    if (!table.tHead || !table.tBodies?.length) {
      return false;
    }

    if (table.querySelector("input, select, textarea, canvas, .signature-pad")) {
      return false;
    }

    return true;
  });
}

function normalizeValue(value) {
  const text = (value || "").trim();
  if (!text) return "";

  const numeric = Number(text.replace(/,/g, ""));
  if (!Number.isNaN(numeric) && /^[-+]?\d[\d,.]*$/.test(text)) {
    return numeric;
  }

  const dateValue = Date.parse(text);
  if (!Number.isNaN(dateValue) && /(\d{4}|\d{1,2}[/-]\d{1,2})/.test(text)) {
    return dateValue;
  }

  return text.toLowerCase();
}

function sortTable(table, columnIndex, direction) {
  const tbody = table.tBodies[0];
  const rows = Array.from(tbody.rows);

  rows.sort((leftRow, rightRow) => {
    const leftText = leftRow.cells[columnIndex]?.innerText || "";
    const rightText = rightRow.cells[columnIndex]?.innerText || "";
    const leftValue = normalizeValue(leftText);
    const rightValue = normalizeValue(rightText);

    if (leftValue < rightValue) return direction === "asc" ? -1 : 1;
    if (leftValue > rightValue) return direction === "asc" ? 1 : -1;
    return 0;
  });

  rows.forEach((row) => tbody.appendChild(row));
}

function decorateTable(table) {
  if (table.dataset.nwTableEnhanced === "true") return;

  table.dataset.nwTableEnhanced = "true";
  table.classList.add("nw-auto-table");
  table.parentElement?.classList.add("nw-auto-table-wrapper");

  const headers = Array.from(table.tHead.rows[0]?.cells || []);
  headers.forEach((header, index) => {
    const headerText = header.innerText.trim().toLowerCase();
    if (!headerText || ACTION_HEADERS.has(headerText)) {
      header.classList.add("nw-unsortable-header");
      return;
    }

    const existing = header.querySelector(".nw-sort-button");
    if (existing) return;

    const label = header.innerHTML;
    header.innerHTML = "";

    const button = document.createElement("button");
    button.type = "button";
    button.className = "nw-sort-button";
    button.innerHTML = `
      <span>${label}</span>
      <span class="nw-sort-indicator" aria-hidden="true">
        <i class="fa-solid fa-arrow-up"></i>
      </span>
    `;

    button.addEventListener("click", () => {
      const currentIndex = Number(table.dataset.nwSortIndex || "-1");
      const currentDirection = table.dataset.nwSortDirection || "asc";
      const nextDirection =
        currentIndex === index && currentDirection === "asc" ? "desc" : "asc";

      table.dataset.nwSortIndex = String(index);
      table.dataset.nwSortDirection = nextDirection;

      headers.forEach((cell) => {
        const indicator = cell.querySelector(".nw-sort-indicator");
        if (indicator) {
          indicator.classList.remove("is-asc", "is-desc");
        }
      });

      const indicator = button.querySelector(".nw-sort-indicator");
      if (indicator) {
        indicator.classList.add(nextDirection === "asc" ? "is-asc" : "is-desc");
      }

      sortTable(table, index, nextDirection);
    });

    header.appendChild(button);
  });
}

export default function GlobalTableEnhancer() {
  useEffect(() => {
    const enhance = () => {
      getSortableTables().forEach(decorateTable);
    };

    enhance();

    const observer = new MutationObserver(() => {
      enhance();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  return null;
}
